import { useState } from 'react';
import { Compass, AlertTriangle, Copy, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { AgenteIaContextoSeletor, type AgenteIaContexto } from '@/components/agentes-ia/AgenteIaContextoSeletor';
import { useAgenteIaJob } from '@/hooks/useAgenteIaJob';
import { useOraculoResultado } from '@/hooks/useOraculoEdital';
import { riscoBadgeVariant } from '@/lib/riskBadge';
import type { Recomendacao } from '@/types/agentesIa';
import type { BadgeVariant } from '@/components/ui/Badge';
import { useViewModeStore } from '@/stores/view-mode';

function EmptyState({ message }: { message: string }) {
  return <p className="text-sm text-ds-text-secondary italic text-center py-8">{message}</p>;
}

const RECOMENDACAO_MAP: Record<Recomendacao, { label: string; variant: BadgeVariant }> = {
  participar: { label: 'Participar', variant: 'success' },
  declinar: { label: 'Declinar', variant: 'danger' },
  analise_manual: { label: 'Análise manual', variant: 'warning' },
};

function CopiarResumoButton({ texto }: { texto: string }) {
  const [copiado, setCopiado] = useState(false);

  const handleCopiar = async () => {
    await navigator.clipboard.writeText(texto);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  };

  return (
    <Button variant="secondary" size="sm" onClick={handleCopiar}>
      {copiado ? (
        <>
          <Check className="h-4 w-4 mr-1.5" /> Copiado
        </>
      ) : (
        <>
          <Copy className="h-4 w-4 mr-1.5" /> Copiar resumo
        </>
      )}
    </Button>
  );
}

function AderenciaBar({ score }: { score: number }) {
  let color = 'bg-ds-danger';
  if (score >= 75) color = 'bg-ds-success';
  else if (score >= 50) color = 'bg-ds-warning';
  else if (score >= 25) color = 'bg-ds-info';

  return (
    <div className="flex items-center gap-3">
      <div className="w-40 h-2 rounded-full bg-ds-surface2 overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-sm font-medium text-ds-text-primary">{score}/100</span>
    </div>
  );
}

function OraculoEdital() {
  const searchParams = new URLSearchParams(window.location.search);
  const initialEmpresaId = Number(searchParams.get('empresaId')) || undefined;
  const initialLicitacaoId = Number(searchParams.get('licitacaoId')) || undefined;
  const { mode, previewEmpresaId } = useViewModeStore();
  const isEmpresaContextual = mode === 'empresa' && initialEmpresaId === previewEmpresaId && Boolean(initialLicitacaoId);
  const [contexto, setContexto] = useState<AgenteIaContexto | null>(null);

  const { enqueue, isEnqueuing, jobStatus, reset } = useAgenteIaJob('oraculo-edital', contexto?.empresaId);
  const { data: resultado, isLoading: resultadoLoading } = useOraculoResultado(
    contexto?.empresaId,
    contexto?.licitacaoId,
    jobStatus?.status
  );

  const isRunning = Boolean(jobStatus && jobStatus.status !== 'completed' && jobStatus.status !== 'failed');

  const handleConsultar = async () => {
    if (!contexto) return;
    reset();
    await enqueue({ licitacaoId: contexto.licitacaoId, documentoId: contexto.documentoId });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-ds-text-primary flex items-center gap-2">
          <Compass className="h-6 w-6 text-ds-brand" />
          Oráculo do Edital
        </h1>
        <p className="text-ds-text-secondary mt-1">
          Recomendação de participação, aderência ao perfil da empresa e riscos identificados no edital.
        </p>
      </div>

      <AgenteIaContextoSeletor onContextoCompleto={setContexto} initialEmpresaId={initialEmpresaId} initialLicitacaoId={initialLicitacaoId} contextoTravado={isEmpresaContextual} empresaRestritaId={mode === 'empresa' ? previewEmpresaId ?? undefined : undefined} />

      <Card>
        <CardHeader>
          <CardTitle>Consultar edital</CardTitle>
          <CardDescription>Compara o edital selecionado contra o perfil técnico da empresa.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            onClick={handleConsultar}
            disabled={!contexto || isEnqueuing || isRunning}
            isLoading={isEnqueuing || isRunning}
          >
            {isEnqueuing || isRunning ? 'Consultando edital...' : 'Consultar edital'}
          </Button>

          {jobStatus?.status === 'failed' && (
            <div className="flex items-start gap-3 rounded-lg border border-ds-danger/20 bg-ds-danger/10 p-4">
              <AlertTriangle className="h-5 w-5 text-ds-danger shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-ds-danger">Falha ao consultar edital</p>
                <p className="text-sm text-ds-text-secondary mt-1">{jobStatus.failedReason}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Resultado</CardTitle>
        </CardHeader>
        <CardContent>
          {!contexto ? (
            <EmptyState message="Selecione empresa, licitação e documento para consultar o edital." />
          ) : resultadoLoading ? (
            <p className="text-center text-ds-text-secondary py-8">Carregando...</p>
          ) : !resultado ? (
            <EmptyState message="Nenhuma consulta feita ainda para esta licitação." />
          ) : (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
                <div>
                  <p className="text-xs text-ds-text-secondary uppercase tracking-wider mb-1">Recomendação</p>
                  <Badge variant={RECOMENDACAO_MAP[resultado.recomendacao].variant}>
                    {RECOMENDACAO_MAP[resultado.recomendacao].label}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-ds-text-secondary uppercase tracking-wider mb-1">Aderência ao perfil</p>
                  <AderenciaBar score={resultado.aderencia_score} />
                </div>
              </div>

              {resultado.resumo_cliente && (
                <div className="rounded-lg border border-ds-border bg-ds-surface2 p-4 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="text-sm font-semibold text-ds-text-primary">Resumo para o cliente</h4>
                    <CopiarResumoButton texto={resultado.resumo_cliente} />
                  </div>
                  <p className="text-sm text-ds-text-primary">{resultado.resumo_cliente}</p>
                </div>
              )}

              {resultado.riscos.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-ds-text-primary">Riscos identificados</h4>
                  {resultado.riscos.map((risco, index) => (
                    <div
                      key={`${risco.tipo}-${index}`}
                      className="rounded-lg border border-ds-border bg-ds-surface2 p-4"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-ds-text-primary">{risco.tipo}</span>
                        <Badge variant={riscoBadgeVariant(risco.severidade)} size="sm">{risco.severidade}</Badge>
                      </div>
                      <p className="text-sm text-ds-text-secondary mt-1">{risco.descricao}</p>
                    </div>
                  ))}
                </div>
              )}

              <div>
                <p className="text-xs text-ds-text-secondary uppercase tracking-wider mb-1">Raciocínio completo</p>
                <p className="text-sm text-ds-text-primary">{resultado.raciocinio}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export { OraculoEdital };
